import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, money } from "@/lib/api";
import { DEFAULT_PACKAGES } from "@/lib/packages";
import { useSeo } from "@/lib/seo";
import { trackPage } from "@/lib/track";
import "@/styles/home.css";

const SITE = "https://99tests.com";
const MCP_URL = "https://99tests.com/api/mcp";

const CLAUDE_CMD = `claude mcp add --transport http 99tests https://99tests.com/api/mcp`;
const MCP_JSON = `{
  "mcpServers": {
    "99tests": {
      "url": "https://99tests.com/api/mcp"
    }
  }
}`;

const TOOLS = [
  ["list_packages", "Packages, prices (INR + GST and USD), sessions and turnaround."],
  ["create_test_job", "Creates a job and returns a hosted checkout link to pay in a browser."],
  ["get_job_status", "Progress for a job token. Poll at most every 10 minutes."],
  ["get_report", "Verified findings for a delivered job, returned as ready-to-paste fix prompts."],
  ["get_sample_report", "An example report, so you can see the format before you pay."],
];

const FAQ = [
  ["Is this automated testing?", "No. Real people run your flows on real devices. Your agent's own self-tests stay as they are."],
  ["Do I need an account?", "No. Each job is paid through Stripe Checkout, and a job token lets your agent fetch status and the report."],
  ["Who pays?", "You do. The agent only hands you the checkout link; it can never charge you."],
  ["What happens to test logins I share?", "They are included in the job only for the assigned testers to use during the session, shared by link, and not retained by 99tests beyond the job."],
  ["What isn't covered?", "Security or penetration testing, load testing, and certification."],
];

const EXAMPLE_FIX_PROMPT =
  'On Redmi 12 · MIUI 14 · Jio 4G, "Cart empties after returning from UPI app". ' +
  "Steps to reproduce: Add 2 items → Checkout → Pay with UPI → switch to PhonePe → approve → return to app. " +
  "Expected: Order confirmed with both items. " +
  "Actual: App restarts on return; cart is empty and no order is created. Money debited. " +
  "Please fix the root cause and add a regression test that covers this case (TC-03).";

function CopyBlock({ code, testid }) {
  const [done, setDone] = useState(false);
  function copy() {
    try {
      navigator.clipboard.writeText(code);
      setDone(true);
      setTimeout(() => setDone(false), 1600);
    } catch (e) {}
  }
  return (
    <div style={{ position: "relative", margin: "10px 0 6px" }}>
      <pre data-testid={testid} style={{ background: "var(--ink)", color: "#fff", padding: "16px 18px", paddingRight: 92, borderRadius: 10, overflowX: "auto", lineHeight: 1.5, margin: 0 }}>{code}</pre>
      <button type="button" onClick={copy} data-testid={`${testid}-copy`}
        style={{ position: "absolute", top: 10, right: 10, border: "1px solid rgba(255,255,255,.3)", background: "rgba(255,255,255,.12)", color: "#fff", borderRadius: 8, padding: "5px 12px", fontSize: 13, cursor: "pointer" }}>
        {done ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

function Header() {
  return (
    <header className="site-header"><div className="wrap">
      <a className="logo" href="/"><i></i>99tests</a>
      <nav>
        <a href="/#how">How it works</a>
        <a href="/devices" data-testid="nav-devices">Devices</a>
        <a href="/pricing">Pricing</a>
        <a href="https://blog.99tests.com/" data-testid="nav-blog">Blog</a>
        <a className="btn sm blue" href="/pricing">Start a job</a>
      </nav>
    </div></header>
  );
}

function Footer() {
  return (
    <footer className="site-footer"><div className="wrap">
      <div>© 2026 99tests · Bengaluru</div>
      <div><a href="/pricing">Pricing</a><a href="/devices">Devices</a><a href="/sample-report/">Sample report</a><a href="https://blog.99tests.com/">Blog</a></div>
      <div><a href="/crowdsourced-testing">Crowdsourced testing</a><a href="/">Home</a><a href="/mcp-server" data-testid="footer-mcp">For AI agents (MCP)</a><a href="mailto:hello@99tests.com">hello@99tests.com</a></div>
    </div></footer>
  );
}

export default function Mcp() {
  const path = "/mcp-server";
  const jsonld = {
    "@context": "https://schema.org",
    "@graph": [
      { "@type": "BreadcrumbList", itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE + "/" },
        { "@type": "ListItem", position: 2, name: "MCP server", item: SITE + path },
      ] },
      { "@type": "FAQPage", mainEntity: FAQ.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) },
    ],
  };
  useSeo({
    title: "MCP server for AI coding agents",
    description: "Connect 99tests to Claude Code, Cursor or any MCP client. Your agent orders a real-device test run and verified bugs come back as fix prompts.",
    path,
    jsonld,
  });
  useEffect(() => { trackPage(path); }, []);

  const { data } = useQuery({ queryKey: ["config"], queryFn: api.config });
  const packages = data?.packages || DEFAULT_PACKAGES;
  const order = ["starter", "standard", "deep"];

  return (
    <>
      <Header />
      <main data-testid="mcp-page">
        <section className="hero"><div className="wrap"><div>
          <div className="ref"><b>Model Context Protocol</b><span className="dot"></span><span>Claude Code · Cursor · any MCP client</span></div>
          <h1>Let your coding agent order <em>real-device testing.</em></h1>
          <p className="lede">Your coding agent already runs headless self-tests. 99tests adds what it can't: real phones and laptops, real Indian networks, and human testers judging whether the app works. Your agent creates the job, you pay through the checkout link, and verified findings come back in the build chat as fix prompts.</p>
        </div></div></section>

        <section className="band"><div className="wrap">
          <div className="section-head"><span className="eyebrow">Connect</span><h2>Add it in one line.</h2></div>
          <p className="lede" style={{ marginBottom: 4 }}>Claude Code</p>
          <CopyBlock code={CLAUDE_CMD} testid="mcp-claude" />
          <p className="lede" style={{ marginTop: 18, marginBottom: 4 }}>Cursor or VS Code (<code>mcp.json</code>)</p>
          <CopyBlock code={MCP_JSON} testid="mcp-json" />
          <p className="lede" style={{ marginTop: 18, marginBottom: 4 }}>Any other MCP client</p>
          <p className="muted" style={{ margin: 0 }}>Add a remote MCP server with the URL <code data-testid="mcp-url">{MCP_URL}</code></p>
          <p className="muted small" style={{ marginTop: 12 }}>The lookups (packages, sample report) are public and need no key. "Create a test job" returns a browser checkout link — payment always happens in your browser, never through the assistant.</p>
        </div></section>

        <section><div className="wrap narrow" style={{ marginLeft: 0 }}>
          <div className="section-head"><span className="eyebrow">Tools</span><h2>What the assistant can do.</h2></div>
          <div style={{ overflowX: "auto" }}>
            <table data-testid="mcp-tools" style={{ width: "100%", borderCollapse: "collapse", fontSize: 15 }}>
              <thead><tr>
                <th style={{ textAlign: "left", padding: "10px 12px", borderBottom: "2px solid var(--line)" }}>Tool</th>
                <th style={{ textAlign: "left", padding: "10px 12px", borderBottom: "2px solid var(--line)" }}>What it does</th>
              </tr></thead>
              <tbody>
                {TOOLS.map(([name, desc]) => (
                  <tr key={name} data-testid={`mcp-tool-${name}`}>
                    <td style={{ padding: "10px 12px", borderBottom: "1px solid var(--line)", whiteSpace: "nowrap" }}><code>{name}</code></td>
                    <td style={{ padding: "10px 12px", borderBottom: "1px solid var(--line)" }}>{desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div></section>

        <section className="band"><div className="wrap">
          <div className="section-head"><span className="eyebrow">Packages</span><h2>Fixed price per job.</h2></div>
          <div style={{ overflowX: "auto" }}>
            <table data-testid="mcp-packages" style={{ width: "100%", borderCollapse: "collapse", fontSize: 15 }}>
              <thead><tr>
                {["Package", "Sessions", "Devices / browsers", "India (incl GST)", "Rest of world", "Report"].map((h) => (
                  <th key={h} style={{ textAlign: "left", padding: "10px 12px", borderBottom: "2px solid var(--line)", whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {order.map((key) => {
                  const p = packages[key];
                  if (!p) return null;
                  return (
                    <tr key={key} data-testid={`mcp-pkg-${key}`}>
                      <td style={{ padding: "10px 12px", borderBottom: "1px solid var(--line)" }}>{p.display_name}</td>
                      <td style={{ padding: "10px 12px", borderBottom: "1px solid var(--line)" }}>{p.sessions} × {p.band === "DEEP" ? "90" : "45"} min</td>
                      <td style={{ padding: "10px 12px", borderBottom: "1px solid var(--line)" }}>{p.devices}</td>
                      <td style={{ padding: "10px 12px", borderBottom: "1px solid var(--line)", whiteSpace: "nowrap" }}>{money(p.prices.INR.display_minor, "INR")} + GST</td>
                      <td style={{ padding: "10px 12px", borderBottom: "1px solid var(--line)", whiteSpace: "nowrap" }}>{money(p.prices.USD.display_minor, "USD")}</td>
                      <td style={{ padding: "10px 12px", borderBottom: "1px solid var(--line)" }}>{p.turnaround_days} business days</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="muted small" style={{ marginTop: 12 }}>Not included: test automation, security/penetration testing, performance/load testing, certification. Full details on <a href="/pricing">/pricing</a>.</p>
        </div></section>

        <section><div className="wrap narrow" style={{ marginLeft: 0 }}>
          <div className="section-head"><span className="eyebrow">What comes back</span><h2>Verified findings, as fix prompts.</h2></div>
          <p className="lede">Each accepted finding comes back with the device, exact steps, expected vs actual, and a ready-to-paste fix prompt. See the full format in the <a href="/sample-report/" data-testid="mcp-sample-link">sample report</a>.</p>
          <p className="muted small" style={{ marginBottom: 6 }}>Example fix prompt from <code>get_report</code>:</p>
          <pre data-testid="mcp-fix-prompt" style={{ background: "var(--ink)", color: "#fff", padding: "16px 18px", borderRadius: 10, overflowX: "auto", lineHeight: 1.55, whiteSpace: "pre-wrap" }}>{EXAMPLE_FIX_PROMPT}</pre>
        </div></section>

        <section className="band"><div className="wrap narrow" style={{ marginLeft: 0 }}>
          <div className="section-head"><span className="eyebrow">Questions</span><h2>About the MCP server.</h2></div>
          <div className="faqbox" data-testid="mcp-faq">
            {FAQ.map(([q, a], i) => (
              <details className="f" key={i} open={i === 0}><summary>{q}</summary><p>{a}</p></details>
            ))}
          </div>
        </div></section>

        <section style={{ borderTop: 0 }}><div className="wrap"><div className="cta">
          <div><h2>Prefer the website?</h2><p>Everything the MCP server exposes is on the site too.</p></div>
          <a className="btn" href="/pricing">See packages</a>
        </div></div></section>
      </main>
      <Footer />
    </>
  );
}
